import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ordersApi, transportApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Alert, Empty, ErrorNote, Loading, PageHeader, StatusBadge, Toolbar } from '@/components/common/Ui'
import Icon from '@/components/common/Icons'
import SearchSelect from '@/components/common/SearchSelect'
import { dateOnly, money } from '@/lib/format'
import { saveBlob } from '@/lib/download'
import { periodRange } from '@/lib/periods'

// Opens on what is still to be sent, any date: a short list, and the one the
// despatch desk is here for. History (dispatched, delivered, everything) is
// read a period at a time — see onStatus.
const DEFAULTS = {
  status: 'to_dispatch',
  period: 'any',
  custom: { date_from: '', date_to: '' },
  transport_service_id: '',
  city: '',
}

// Statuses whose orders pile up for ever: chosen, they start on This week.
const HISTORY = new Set(['', 'dispatched', 'delivered'])

// Hidden, not removed: the Excel export. The API still serves it
// (format=xlsx); flip this back to true to offer it again.
const SHOW_EXCEL = false

/**
 * The transporters customers are choosing now first — by their newest
 * order — then the ones never used, A–Z.
 */
const byLatestUse = (services) =>
  [...(services ?? [])].sort(
    (a, b) =>
      (b.last_used_at ?? '').localeCompare(a.last_used_at ?? '') ||
      a.name.localeCompare(b.name),
  )

const kg = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? `${n.toLocaleString('en-IN', { maximumFractionDigits: 3 })} kg` : '—'
}

/**
 * Orders by the transport service taking them — what the despatch desk books
 * the lorries from, and the record of what went with whom.
 *
 * Summary first: each transport is a collapsed card with its order count,
 * weight and value, and its orders are fetched only when the card is opened.
 * The downloads ignore that — they carry every order the filters match.
 */
export default function TransportLoads() {
  const [filters, setFilters] = useState(DEFAULTS)
  const [open, setOpen] = useState(() => new Set())
  const [downloading, setDownloading] = useState('')
  const [error, setError] = useState('')

  const range = filters.period === 'custom' ? filters.custom : periodRange(filters.period) ?? {}
  const params = Object.fromEntries(
    Object.entries({
      status: filters.status,
      date_from: range.date_from,
      date_to: range.date_to,
      transport_service_id: filters.transport_service_id,
      city: filters.city.trim(),
    }).filter(([, v]) => v !== undefined && String(v) !== ''),
  )
  const narrowed =
    filters.status !== DEFAULTS.status ||
    filters.period !== DEFAULTS.period ||
    filters.transport_service_id !== '' ||
    filters.city.trim() !== ''

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['transport-loads', 'summary', params],
    queryFn: () => ordersApi.transportLoads({ ...params, summary: true }),
  })
  const { data: services } = useQuery({
    queryKey: ['transport-services', { is_active: true }],
    queryFn: () => transportApi.list({ is_active: true }),
  })

  const set = (patch) => {
    setFilters((f) => ({ ...f, ...patch }))
    setOpen(new Set())               // other orders now: start collapsed again
  }

  // Choosing history over any date would load every order ever sent, so it
  // starts on This week; the period can still be widened by hand.
  const onStatus = (status) =>
    set(
      HISTORY.has(status) && filters.period === 'any'
        ? { status, period: 'this_week' }
        : { status },
    )

  const toggle = (key) =>
    setOpen((s) => {
      const next = new Set(s)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const download = async (format) => {
    setError('')
    setDownloading(format)
    try {
      const { blob, filename } = await ordersApi.exportTransportLoads(params, format)
      saveBlob(blob, filename)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setDownloading('')
    }
  }

  return (
    <div className="loads-page">
      <PageHeader
        title="Transport Reports"
        subtitle="Orders by the transport service taking them"
        actions={
          // One choice, as on the Orders list: a format, then back to
          // 'Export' so it reads as an action. Covers the filters below —
          // every order, whichever cards are open.
          <select
            aria-label="Export the transport report"
            className="export-select"
            value=""
            disabled={Boolean(downloading) || !data?.order_count}
            onChange={(e) => {
              const format = e.target.value
              if (format) download(format)
            }}
          >
            <option value="">{downloading ? 'Preparing…' : 'Export'}</option>
            <option value="csv">CSV</option>
            {SHOW_EXCEL && <option value="xlsx">Excel</option>}
            <option value="pdf">PDF</option>
          </select>
        }
      />

      <Toolbar>
        <select value={filters.status} onChange={(e) => onStatus(e.target.value)} aria-label="Status">
          <option value="to_dispatch">To be dispatched</option>
          <option value="">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="packed">Packed</option>
          <option value="dispatched">Dispatched</option>
          <option value="delivered">Delivered</option>
        </select>
        <select value={filters.period} onChange={(e) => set({ period: e.target.value })} aria-label="Period">
          <option value="any">Any date</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="this_week">This week</option>
          <option value="last_week">Last week</option>
          <option value="this_month">This month</option>
          <option value="last_month">Last month</option>
          <option value="custom">Custom dates</option>
        </select>
        {filters.period === 'custom' && (
          <>
            <label className="loads-date">
              <span>From</span>
              <input
                type="date"
                value={filters.custom.date_from}
                onChange={(e) => set({ custom: { ...filters.custom, date_from: e.target.value } })}
              />
            </label>
            <label className="loads-date">
              <span>To</span>
              <input
                type="date"
                value={filters.custom.date_to}
                onChange={(e) => set({ custom: { ...filters.custom, date_to: e.target.value } })}
              />
            </label>
          </>
        )}
        {range.date_from && filters.period !== 'custom' && (
          <span className="muted sales-period">
            {dateOnly(range.date_from)}
            {range.date_to !== range.date_from && ` – ${dateOnly(range.date_to)}`}
          </span>
        )}
        {/* Searchable: the list grows with every transporter a customer adds. */}
        <SearchSelect
          ariaLabel="Transport service"
          placeholder="Search transport services…"
          value={filters.transport_service_id}
          onChange={(v) => set({ transport_service_id: String(v) })}
          options={[
            { value: '', label: 'All transport services' },
            { value: '0', label: 'No transport chosen' },
            { value: '-1', label: 'Store pickup' },
            ...byLatestUse(services).map((s) => ({
              value: String(s.id),
              label: s.city ? `${s.name} — ${s.city}` : s.name,
            })),
          ]}
        />
        <input
          type="search"
          placeholder="Delivery city"
          value={filters.city}
          onChange={(e) => set({ city: e.target.value })}
        />
        {narrowed && (
          <button
            type="button"
            className="btn btn-sm btn-icon"
            title="Back to: to be dispatched, any date"
            aria-label="Clear all filters"
            onClick={() => set(DEFAULTS)}
          >
            <Icon name="clearFilter" />
          </button>
        )}
      </Toolbar>

      <Alert tone="danger" message={error} onClose={() => setError('')} />

      {isLoading && <Loading />}
      {isError && <ErrorNote message="Could not load the transport report" onRetry={refetch} />}

      {data && data.order_count === 0 && (
        <Empty
          title={narrowed ? 'No orders match these filters' : 'Nothing waiting to be dispatched'}
          hint={narrowed ? undefined : 'Confirmed and packed orders appear here until they are dispatched.'}
        />
      )}

      {data && data.order_count > 0 && (
        <>
          <div className="loads-summary">
            <span><strong>{data.order_count}</strong> orders</span>
            <span><strong>{data.groups.length}</strong> transport groups</span>
            <span><strong>{kg(data.weight_kg)}</strong> in all</span>
            <span><strong>{money(data.grand_total)}</strong></span>
          </div>

          {data.groups.map((group) => {
            // -1: the store-pickup orders, which travel with no transporter.
            const key = group.pickup ? -1 : group.transport?.id ?? 0
            const isOpen = open.has(key)
            return (
              <section
                key={key}
                className={`panel loads-group${group.transport || group.pickup ? '' : ' loads-unassigned'}`}
              >
                <button
                  type="button"
                  className="loads-group-head loads-group-toggle"
                  aria-expanded={isOpen}
                  onClick={() => toggle(key)}
                >
                  <div>
                    <div className="panel-title loads-group-name">
                      <span className="sales-caret">{isOpen ? '▾' : '▸'} </span>
                      {group.transport
                        ? group.transport.name
                        : group.pickup
                          ? 'Store pickup'
                          : 'No transport chosen'}
                    </div>
                    {group.transport ? (
                      <div className="muted">
                        {group.transport.contact_numbers.join(' / ')}
                        {group.transport.city && ` · ${group.transport.city}`}
                      </div>
                    ) : group.pickup ? (
                      <div className="muted">Collected by the customer from the store.</div>
                    ) : (
                      <div className="muted">Choose a transport service on these orders.</div>
                    )}
                  </div>
                  <div className="loads-group-totals">
                    <span>{group.order_count} orders</span>
                    <strong>{kg(group.weight_kg)}</strong>
                    <span>{money(group.grand_total)}</span>
                  </div>
                </button>

                {isOpen && <GroupOrders params={params} transportId={key} />}
              </section>
            )
          })}
        </>
      )}
    </div>
  )
}

/** One transport's orders, fetched the first time its card is opened. */
function GroupOrders({ params, transportId }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['transport-loads', 'orders', params, transportId],
    queryFn: () => ordersApi.transportLoads({ ...params, transport_service_id: transportId }),
  })

  if (isLoading) return <Loading />
  if (isError) return <ErrorNote message="Could not load these orders" onRetry={refetch} />
  const orders = data?.groups?.[0]?.orders ?? []

  return (
    <div className="loads-table-wrap">
      <table className="table loads-table">
        <thead>
          <tr>
            <th>Order</th>
            <th>Customer</th>
            <th>Delivery city</th>
            <th>Items</th>
            <th className="right">Load</th>
            <th className="right">Amount</th>
            <th>Reference No.</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id}>
              <td>
                <Link to={`/orders/${order.id}`}>{order.order_number}</Link>
                <div className="cell-sub">
                  <StatusBadge status={order.status} /> {dateOnly(order.created_at)}
                </div>
              </td>
              <td>
                {order.customer}
                {order.business_name && <div className="cell-sub">{order.business_name}</div>}
                {order.customer_phone && (
                  <div className="cell-sub">
                    <a href={`tel:+${order.customer_phone}`}>{order.customer_phone}</a>
                  </div>
                )}
              </td>
              <td>{order.delivery_city || <span className="muted">—</span>}</td>
              <td>
                {order.items.map((item, i) => (
                  <div key={`${item.code}-${i}`} className="loads-item">
                    {item.product}
                    <span className="muted">
                      {' '}
                      · {Number(item.quantity)} {String(item.unit).toLowerCase()}
                      {item.weight_kg != null &&
                        String(item.unit).toUpperCase() !== 'KILOGRAMS' &&
                        ` (${kg(item.weight_kg)})`}
                    </span>
                  </div>
                ))}
              </td>
              <td className="right nowrap">
                {kg(order.weight_kg)}
                {order.unweighed_lines > 0 && (
                  <div className="cell-sub">+{order.unweighed_lines} by count</div>
                )}
              </td>
              <td className="right nowrap">{money(order.grand_total)}</td>
              <td className="mono">{order.transport_reference || <span className="muted">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
